import type {
  AutoClaimedWinStreakTowerDto,
  ClaimWinStreakTowerFloorResponse,
  WinStreakTowerBalancesDto,
  WinStreakTowerDto,
  WinStreakTowerPayloadDto,
  WinStreakTowerProgressDto,
} from "../api/dto/win_streak_tower.dto.js";
import {
  getWinStreakTowerConditional,
  claimWinStreakTowerFloor,
  getWinStreakTower,
  resetWinStreakTowerProgress,
} from "../api/requests/win_streak_tower.requests.js";
import { CumulativeBoosterService } from "../cumulative_boosters/cumulative_booster_service.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { infiniteHeartsUntilMs, serverUntilMs } from "../api/player_sync.js";
import { NetworkManager } from "../network_manager.js";
import { isNetworkishError } from "../utils/network_errors.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { EventPayloadListeners, eventPayloadKey, type EventPayloadListener } from "../api/event_payload_key.js";
import { ServerClock } from "../offline/server_clock.js";
import { OfflineJournal } from "../offline/offline_journal.js";
import { applyLocalEventUnlock, isEventProgressActive } from "../offline/local_event_unlock.js";
import { shouldGoLocal } from "../offline/offline_policy.js";
import { OfflineEconomy } from "../offline/offline_economy.js";
import { LocalMetrics } from "../offline/local_metrics.js";
import { WinStreakTowerAnalytics } from "./win_streak_tower_analytics.js";

const CACHE_TTL_MS = 30_000;
const EXPIRED_RETRY_MS = 15_000;
const PERSIST_KEY = "win_streak_tower_v1";
const PROGRESS_LEAD_LEVELS = 1;

interface PersistedTower
{
  payload: WinStreakTowerPayloadDto;
  fetched_at: number;
  etag?: string | null;
}

/** Server-authoritative client state for the rotating win-streak tower. */
class WinStreakTowerServiceImpl
{
  private payload: WinStreakTowerPayloadDto | null = null;
  private etag: string | null = null;
  private fetched_at = 0;
  private pending_fetch: Promise<WinStreakTowerPayloadDto | null> | null = null;
  private readonly listeners = new EventPayloadListeners<WinStreakTowerPayloadDto>();
  private pending_auto_claimed: AutoClaimedWinStreakTowerDto[] = [];

  constructor()
  {
    const stored = PersistentCache.read<PersistedTower>( PERSIST_KEY );
    if ( stored?.payload && typeof stored.payload === "object" )
    {
      this.payload = stored.payload;
      this.etag = typeof stored.etag === "string" ? stored.etag : null;
      this.fetched_at = 0;
    }
  }

  public fetchedAgoMs(): number
  {
    return this.fetched_at > 0 ? Date.now() - this.fetched_at : Number.POSITIVE_INFINITY;
  }

  public getCached(): WinStreakTowerPayloadDto | null
  {
    this.unlockLocally();
    return this.payload;
  }

  public onChange( listener: EventPayloadListener<WinStreakTowerPayloadDto> ): () => void
  {
    return this.listeners.add( listener );
  }

  public isShowable( payload: WinStreakTowerPayloadDto | null ): payload is WinStreakTowerPayloadDto
  {
    return !!payload?.enabled && !!payload.tower && payload.tower.floors.length >= 2;
  }

  private unlockLocally(): void
  {
    if ( applyLocalEventUnlock( this.payload ) )
    {
      console.info( "[WinStreakTower] unlocked locally by level" );
      this.persist();
    }
  }

  public async fetch( force = false ): Promise<WinStreakTowerPayloadDto | null>
  {
    this.unlockLocally();
    const age = Date.now() - this.fetched_at;
    const max_age = this.isPeriodOver() ? EXPIRED_RETRY_MS : CACHE_TTL_MS;
    if ( !force && this.payload && age < max_age )
      return this.payload;

    if ( !NetworkManager.isOnline() )
      return this.payload;

    if ( this.pending_fetch ) return this.pending_fetch;

    this.pending_fetch = this.fetchInternal();
    try
    {
      return await this.pending_fetch;
    }
    finally
    {
      this.pending_fetch = null;
    }
  }

  private async fetchInternal(): Promise<WinStreakTowerPayloadDto | null>
  {
    try
    {
      const res = await getWinStreakTowerConditional( this.etag );
      this.fetched_at = Date.now();
      if ( res.status === 304 )
        return this.payload;

      const payload = res.data;
      const changed = !this.payload || eventPayloadKey( this.payload ) !== eventPayloadKey( payload );
      this.payload = payload;
      this.etag = res.etag;
      this.persist();
      if ( payload.enabled ) WinStreakTowerAnalytics.observe( payload.tower );

      if ( payload.auto_claimed.length > 0 )
      {
        this.pending_auto_claimed.push( ...payload.auto_claimed );
        if ( payload.balances ) this.applyBalances( payload.balances );
        WinStreakTowerAnalytics.autoClaimed( payload.auto_claimed );
      }

      if ( changed )
        this.listeners.notify( payload );
      return payload;
    }
    catch ( e )
    {
      console.warn( "Win-streak tower fetch failed:", e );
      return this.payload;
    }
  }

  public consumeAutoClaimed(): AutoClaimedWinStreakTowerDto[]
  {
    const pending = this.pending_auto_claimed;
    this.pending_auto_claimed = [];
    return pending;
  }

  /** Debug-only reset that drops all server snapshots and reloads this period. */
  public async resetProgressForDebug(): Promise<WinStreakTowerPayloadDto | null>
  {
    if ( this.pending_fetch )
      await this.pending_fetch;

    await resetWinStreakTowerProgress();
    this.payload = null;
    this.etag = null;
    this.fetched_at = 0;
    this.pending_auto_claimed = [];
    PersistentCache.remove( PERSIST_KEY );
    return this.fetch( true );
  }

  /** Applies the compact state returned by level-complete / level-failed. */
  public applyProgress( update: WinStreakTowerProgressDto | null ): void
  {
    if ( update === null ) return;

    const tower = this.payload?.tower;
    if ( !tower )
    {
      void this.fetch( true );
      return;
    }

    tower.current_floor_id = update.current_floor_id;
    tower.wins_progress = update.wins_progress;
    tower.completed = update.tower_completed;

    for ( const floor of tower.floors )
    {
      const reached = update.current_floor_id !== null && floor.id <= update.current_floor_id;
      floor.reached = reached;
      floor.claimable = reached && !floor.claimed;
    }

    tower.next_floor_id = update.tower_completed
      ? null
      : tower.floors.find( floor => !floor.reached )?.id ?? null;
    this.persist();
    if ( this.payload?.enabled )
    {
      WinStreakTowerAnalytics.progress( tower, update );
      WinStreakTowerAnalytics.observe( tower );
    }
  }

  public applyLocalWin(): WinStreakTowerProgressDto | null
  {
    const tower = this.progressTower();
    if ( !tower ) return null;

    if ( tower.next_floor_id === null || tower.completed )
      return this.toProgress( tower, { changed: false, new_floor: false, streak_reset: false } );

    const index = tower.floors.findIndex( floor => floor.id === tower.next_floor_id );
    if ( index < 0 )
      return this.toProgress( tower, { changed: false, new_floor: false, streak_reset: false } );

    const floor = tower.floors[index];
    const wins = tower.wins_progress + 1;
    if ( wins < floor.wins_required )
    {
      tower.wins_progress = wins;
      this.persist();
      return this.toProgress( tower, { changed: true, new_floor: false, streak_reset: false } );
    }

    floor.reached = true;
    floor.claimable = !floor.claimed;
    tower.current_floor_id = floor.id;
    const next = tower.floors[index + 1] ?? null;
    tower.next_floor_id = next?.id ?? null;
    tower.wins_progress = 0;
    tower.completed = next === null;
    this.persist();
    if ( this.payload?.enabled )
      LocalMetrics.report( [ { metric: "tower_floors", amount: 1 } ] );
    return this.toProgress( tower, { changed: true, new_floor: true, streak_reset: false } );
  }

  public applyLocalLoss(): WinStreakTowerProgressDto | null
  {
    const tower = this.progressTower();
    if ( !tower ) return null;

    const changed = tower.next_floor_id !== null && tower.wins_progress > 0;
    if ( changed )
    {
      tower.wins_progress = 0;
      this.persist();
    }
    return this.toProgress( tower, { changed, new_floor: false, streak_reset: changed } );
  }

  public async claimFloor( floor_id: number ): Promise<ClaimWinStreakTowerFloorResponse | null>
  {
    if ( shouldGoLocal() )
      return this.claimFloorLocally( floor_id );

    try
    {
      const response = await claimWinStreakTowerFloor( floor_id );
      this.applyBalances( response.balances );
      if ( this.payload ) this.payload.tower = response.tower;
      this.persist();
      WinStreakTowerAnalytics.claimed( response.tower, floor_id, response.rewards );
      return response;
    }
    catch ( e )
    {
      if ( isNetworkishError( e ) )
        return this.claimFloorLocally( floor_id );
      console.warn( `Win-streak tower floor claim failed (${floor_id}):`, e );
      void this.fetch( true );
      return null;
    }
  }

  private claimFloorLocally( floor_id: number ): ClaimWinStreakTowerFloorResponse | null
  {
    const tower = this.activeTower();
    const floor = tower?.floors.find( entry => entry.id === floor_id );
    if ( !tower || !floor || !floor.reached || floor.claimed )
      return null;

    floor.claimed = true;
    floor.claimable = false;
    OfflineEconomy.grantRewards( floor.rewards );
    OfflineJournal.append( "tower_floor_claim", { floor_id, period_start: tower.period_start } );
    this.persist();
    WinStreakTowerAnalytics.claimed( tower, floor_id, floor.rewards );

    return {
      ok: true,
      floor_id,
      rewards: floor.rewards,
      tower,
      balances: this.localBalances(),
    };
  }

  private activeTower(): WinStreakTowerDto | null
  {
    this.unlockLocally();
    const tower = this.payload?.enabled ? this.payload.tower : null;
    if ( !tower || this.isPeriodOver() ) return null;
    return tower;
  }

  private progressTower(): WinStreakTowerDto | null
  {
    this.unlockLocally();
    const tower = isEventProgressActive( this.payload, PROGRESS_LEAD_LEVELS ) ? this.payload?.tower ?? null : null;
    if ( !tower || this.isPeriodOver() ) return null;
    return tower;
  }

  private toProgress(
    tower: WinStreakTowerDto,
    flags: { changed: boolean; new_floor: boolean; streak_reset: boolean }
  ): WinStreakTowerProgressDto
  {
    const next = tower.floors.find( floor => floor.id === tower.next_floor_id ) ?? null;
    return {
      ...flags,
      current_floor_id: tower.current_floor_id,
      wins_progress: tower.wins_progress,
      wins_required: next?.wins_required ?? null,
      tower_completed: tower.completed,
    };
  }

  private localBalances(): WinStreakTowerBalancesDto
  {
    const stats = StatisticsStorage.load();
    return {
      coins: stats.coins,
      hearts: stats.hearts,
      infinite_hearts_until: stats.infinite_hearts_until > 0 ? new Date( stats.infinite_hearts_until ).toISOString() : null,
      no_ads_until: stats.no_ads_until > 0 ? new Date( stats.no_ads_until ).toISOString() : null,
      boosters: CumulativeBoosterService.getInventory(),
    };
  }

  private applyBalances( balances: WinStreakTowerBalancesDto ): void
  {
    const stats = StatisticsStorage.load();
    StatisticsStorage.update( {
      coins: balances.coins,
      hearts: Math.max( stats.hearts, balances.hearts ),
      infinite_hearts_until: Math.max(
        stats.infinite_hearts_until,
        infiniteHeartsUntilMs( balances.infinite_hearts_until )
      ),
      no_ads_until: Math.max( stats.no_ads_until, serverUntilMs( balances.no_ads_until ) ),
      fireworks: balances.boosters.fireworks,
      multiballs: balances.boosters.multi_balls,
      big_balls: balances.boosters.big_balls,
      infinite_balls: balances.boosters.infinite_balls,
    } );
    CumulativeBoosterService.applyInventory( balances.boosters );
  }

  private persist(): void
  {
    if ( !this.payload ) return;
    const stored: PersistedTower = { payload: this.payload, fetched_at: this.fetched_at, etag: this.etag };
    PersistentCache.write( PERSIST_KEY, stored );
  }

  public isPeriodOver(): boolean
  {
    const end = this.payload?.tower?.period_end;
    if ( !end ) return false;
    const end_ms = Date.parse( end );
    return Number.isFinite( end_ms ) && ServerClock.nowMs() >= end_ms;
  }
}

export const WinStreakTowerService = new WinStreakTowerServiceImpl();
